import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './RoomConfigurationPage.css';

const RoomConfigurationPage = () => {
  const { t } = useTranslation();

  // Calcolo date iniziali
  const today = new Date();
  const localToday = new Date(today.getTime() - (today.getTimezoneOffset() * 60000));
  const todayString = localToday.toISOString().split('T')[0];
  const tomorrowDate = new Date(today);
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const localTomorrow = new Date(tomorrowDate.getTime() - (tomorrowDate.getTimezoneOffset() * 60000));
  const tomorrowString = localTomorrow.toISOString().split('T')[0];

  // Stati configurazione
  const [startDate, setStartDate] = useState(todayString);
  const [endDate, setEndDate] = useState(tomorrowString);
  const [numberOfPeople, setNumberOfPeople] = useState(1);
  const [bedType, setBedType] = useState('double');
  const [extras, setExtras] = useState('');
  const [includeCrib, setIncludeCrib] = useState(false);
  const [hasPet, setHasPet] = useState(false);
  const [petType, setPetType] = useState('');


  const getMinCheckoutDate = () => {
    if (startDate) {
      const start = new Date(startDate);
      start.setDate(start.getDate() + 1);
      return start.toISOString().split('T')[0];
    }
    return tomorrowString;
  };

  const minCheckoutDate = getMinCheckoutDate();

  const handleStartDateChange = (e) => {
    const newStart = e.target.value;
    setStartDate(newStart);
    if (endDate && newStart >= endDate) {
      const start = new Date(newStart);
      start.setDate(start.getDate() + 1);
      setEndDate(start.toISOString().split('T')[0]);
    }
  };

  // Rileva se è mobile
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Prepara il corpo dell'email
  const prepareEmailContent = () => {
    const dates = startDate && endDate ? `${startDate} al ${endDate}` : 'Date non selezionate';

    const bedTypeLabel = bedType === 'single' ? t('configBedSingle') : t('configBedDouble');

    let petInfo = '';
    if (hasPet && petType) {
      const petTypeTranslation = {
        'dog': t('configPetDog'),
        'cat': t('configPetCat'),
        'bird': t('configPetBird'),
        'other': t('configPetOther'),
      };
      petInfo = petTypeTranslation[petType] || petType;
    }

    const emailTemplate = t('configEmailBody')
      .replace('{{room}}', dates)
      .replace('{{people}}', numberOfPeople)
      .replace('{{bedType}}', bedTypeLabel)
      .replace('{{extras}}', extras || 'Nessuno')
      .replace('{{crib}}', includeCrib ? 'Sì (+€15,00)' : 'No')
      .replace('{{pet}}', hasPet ? 'Sì' : 'No')
      .replace('{{petType}}', petInfo ? `Tipo: ${petInfo}` : '');

    return emailTemplate;
  };

  // Invia email
  const handleSendEmail = () => {
    if (!startDate || !endDate) {
      alert("Seleziona le date di check-in e check-out prima di procedere.");
      return;
    }

    if (hasPet && !petType) {
      alert(t('configErrorSelectPetType'));
      return;
    }

    const subject = encodeURIComponent(t('configEmailSubject'));
    const body = encodeURIComponent(prepareEmailContent());

    if (isMobile()) {
      window.location.href = `mailto:info@anticopioldefeltre.it?subject=${subject}&body=${body}`;
    } else {
      const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=info@anticopioldefeltre.it&su=${subject}&body=${body}`;
      window.open(gmailLink, '_blank');
    }
  };

  return (
    <div className="room-configuration-page">
      {/* Header */}
      <section className="page-header room-config-header">
        <div className="header-content">
          <h1>{t('roomConfigTitle')}</h1>
          <p>{t('roomConfigDescription')}</p>
        </div>
      </section>

      {/* Configurazione */}
      <section className="configuration-container">
        <div className="config-wrapper">
          {/* Form */}
          <div className="config-form-section">
            {/* Selezione Date */}
            <div className="form-group date-picker-group">
              <label htmlFor="startDate">Check-in</label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={handleStartDateChange}
                className="config-input"
                min={todayString}
              />
            </div>
            <div className="form-group date-picker-group">
              <label htmlFor="endDate">Check-out</label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="config-input"
                min={minCheckoutDate}
              />
            </div>

            {/* Numero Persone */}
            <div className="form-group">
              <label htmlFor="numberOfPeople">{t('configNumberOfPeople')}</label>
              <div className="number-picker-container">
                <button 
                  type="button" 
                  className="number-picker-btn"
                  onClick={() => setNumberOfPeople(Math.max(1, numberOfPeople - 1))}
                >
                  -
                </button>
                <input
                  type="number"
                  id="numberOfPeople"
                  min="1"
                  value={numberOfPeople}
                  onChange={(e) => {
                    const value = Math.max(parseInt(e.target.value) || 1, 1);
                    setNumberOfPeople(value);
                  }}
                  className="config-input text-center"
                />
                <button 
                  type="button" 
                  className="number-picker-btn"
                  onClick={() => setNumberOfPeople(numberOfPeople + 1)}
                >
                  +
                </button>
              </div>
            </div>

            {/* Tipo di Letto */}
            <div className="form-group">
              <label htmlFor="bedType">{t('configBedType')}</label>
              <select
                id="bedType"
                value={bedType}
                onChange={(e) => setBedType(e.target.value)}
                className="config-input"
              >
                <option value="single">{t('configBedSingle')}</option>
                <option value="double">{t('configBedDouble')}</option>
              </select>
            </div>

            {/* Extra */}
            <div className="form-group">
              <label htmlFor="extras">{t('configExtras')}</label>
              <textarea
                id="extras"
                value={extras}
                onChange={(e) => setExtras(e.target.value)}
                placeholder={t('configExtras')}
                className="config-input config-textarea"
                rows="3"
              />
            </div>

            {/* Culla */}
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeCrib}
                  onChange={(e) => setIncludeCrib(e.target.checked)}
                  className="config-checkbox"
                />
                <span>{t('configCrib')}</span>
              </label>
            </div>

            {/* Animale domestico */}
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={hasPet}
                  onChange={(e) => setHasPet(e.target.checked)}
                  className="config-checkbox"
                />
                <span>{t('configPet')}</span>
              </label>
            </div>

            {/* Tipo animale */}
            {hasPet && (
              <div className="form-group">
                <label htmlFor="petType">{t('configPetType')}</label>
                <select
                  id="petType"
                  value={petType}
                  onChange={(e) => setPetType(e.target.value)}
                  className="config-input"
                >
                  <option value="">{t('configSelectPetType')}</option>
                  <option value="dog">{t('configPetDog')}</option>
                  <option value="cat">{t('configPetCat')}</option>
                  <option value="bird">{t('configPetBird')}</option>
                  <option value="other">{t('configPetOther')}</option>
                </select>
              </div>
            )}

            {/* Pulsante Invia Email */}
            <button onClick={handleSendEmail} className="btn-send-email">
              Prenota il tuo soggiorno
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RoomConfigurationPage;
